import React, { useState } from 'react'
import { Element, Button, Tabs } from 'react-bulma-components'
import { Field, Form, Formik, FormikHelpers } from 'formik'
import SelectField from '../../../form/fields/select'
import {
  AvailableWorkerWithPostSuggestionData,
  WorkersRequestPostsSuggestions,
} from '../../../../hooks/use-workers-request-posts-suggestions'
import { WorkersRequest, WorkersRequestPost } from '../../../../api/workers-requests'
import SelectPostsList from './select-posts-list'
import { compareDocsById } from '../../../../utils/documents'
import OverlayLoader from '../../../loader/overlay-loader'

interface MultipleAssignsFormProps {
  onAutoAssign: (selectedWorkerIds: string[], posts: WorkersRequest['posts']) => Promise<void>
  suggestions: WorkersRequestPostsSuggestions
  workersRequest: WorkersRequest
}

interface FormValues {
  selectedWorkerIds: string[]
  currentJobTile: string
}

export interface SuggestedWorker extends AvailableWorkerWithPostSuggestionData {
  isDesired: boolean
  hasCorrespondingMission: boolean
}

// Types pour les onglets
type TabType = 'desired' | 'corresponding' | 'available'

const TABS: { label: string; value: TabType; explanation: string }[] = [
  {
    label: "Intérimaires désirés par l'EU",
    value: 'desired',
    explanation: "Liste des intérimaires disponibles et désirés par l'EU pour ce poste.",
  },
  {
    label: 'Missions correspondantes',
    value: 'corresponding',
    explanation:
      'Liste des intérimaires disponibles et ayant une mission correspondante dans les 2 derniers mois.',
  },
  {
    label: 'Disponibles',
    value: 'available',
    explanation: 'Liste des intérimaires disponibles pour ce poste.',
  },
]

const MultipleAssignsForm: React.FC<MultipleAssignsFormProps> = ({
  onAutoAssign,
  suggestions,
  workersRequest,
}) => {
  // État local pour l'onglet actif
  const [activeTab, setActiveTab] = useState<TabType>('desired')

  // Initialiser les données du formulaire
  const initialValues: FormValues = {
    selectedWorkerIds: [],
    currentJobTile: suggestions.jobTitles[0]?._id || '',
  }

  // Fonction de validation native Formik
  const validate = (values: FormValues) => {
    const errors: any = {}
    const selectedCount = values.selectedWorkerIds.filter(id => id).length
    const maxCount = suggestions.availablePosts.length

    if (selectedCount === 0) {
      errors.selectedWorkerIds = 'Veuillez sélectionner au moins un post à auto-assigner'
    } else if (selectedCount > maxCount) {
      errors.selectedWorkerIds = `Vous ne pouvez pas sélectionner plus de ${maxCount} poste(s)`
    }

    return errors
  }

  const handleSubmit = async (values: FormValues, formikHelpers: FormikHelpers<FormValues>) => {
    try {
      const selectedWorkerIds = values.selectedWorkerIds.filter(Boolean)
      await onAutoAssign(selectedWorkerIds, suggestions.availablePosts)
      formikHelpers.resetForm()
    } catch (error) {
      console.error('Error during auto-assignation:', error)
    }
  }

  // Filtrer les travailleurs selon l'onglet actif
  const getCurrentWorkersData = React.useCallback(
    (type: TabType, currentJobTileId: string) => {
      const counts: Record<TabType, number> = {
        desired: 0,
        corresponding: 0,
        available: suggestions.workers.length,
      }

      let workers = suggestions.workers.map(worker => {
        const isDesired =
          Boolean(currentJobTileId) && worker.desiredJobTitleIds.includes(currentJobTileId)
        const hasCorrespondingMission =
          Boolean(currentJobTileId) &&
          worker.missionCorrespondingJobTitleIds.includes(currentJobTileId)
        if (isDesired) {
          counts.desired++
        }
        if (hasCorrespondingMission) {
          counts.corresponding++
        }
        return {
          ...worker,
          isDesired,
          hasCorrespondingMission,
          isMarkedAsAvailable: true,
        }
      })

      if (type !== 'available') {
        workers = workers.filter(worker => {
          if (type === 'desired') {
            return worker.isDesired
          } else if (type === 'corresponding') {
            return worker.hasCorrespondingMission
          }
          return true
        })
      }

      return { workers, counts }
    },
    [suggestions.workers],
  )

  const getCurrentPosts = React.useCallback(
    (currentJobTileId: string): WorkersRequestPost[] => {
      return suggestions.availablePosts.filter(post =>
        compareDocsById(post.jobTitle, currentJobTileId),
      )
    },
    [suggestions.availablePosts],
  )

  const currentTab = React.useMemo(() => {
    return TABS.find(tab => tab.value === activeTab)
  }, [activeTab])

  const availablePostsByJobTitle = React.useMemo(() => {
    return suggestions.availablePosts.reduce(
      (acc, post) => {
        const jobTitleId = post.jobTitle._id
        if (!acc[jobTitleId]) {
          acc[jobTitleId] = 0
        }
        acc[jobTitleId]++
        return acc
      },
      {} as Record<string, number>,
    )
  }, [suggestions.availablePosts])

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize
      validate={validate}
      onSubmit={handleSubmit}
    >
      {({ isSubmitting, values, setValues }) => {
        const selectedPostsCount = values.selectedWorkerIds.filter(id => id).length
        const workersData = getCurrentWorkersData(activeTab, values.currentJobTile)
        const workersFilteredByJobTitle = workersData.workers
        const availablePostsFilteredByJobTitle = getCurrentPosts(values.currentJobTile)
        const submitButtonLabel = isSubmitting
          ? 'Création des missions en cours...'
          : `Créer ${selectedPostsCount} mission(s)`
        const submitButtonDisabled = isSubmitting || selectedPostsCount === 0

        return (
          <Form>
            <Element>
              {/* Sélecteur de métier */}
              <Element mb={4}>
                <Field
                  label={`Choisir le métier`}
                  component={SelectField}
                  fullwidth
                  name="currentJobTile"
                  options={suggestions.jobTitles.map((jobTitle: any) => ({
                    label: `${jobTitle.name} - ${availablePostsByJobTitle[jobTitle._id]} poste(s) à pourvoir`,
                    value: jobTitle._id,
                  }))}
                  help={`La création de missions multiples est possible uniquement pour un même métier.`}
                />
              </Element>

              {/* Onglets */}
              <Tabs className="mb-4">
                {TABS.map(tab => (
                  <Tabs.Tab
                    key={tab.value}
                    active={activeTab === tab.value}
                    onClick={e => {
                      e.preventDefault()
                      setValues({
                        ...values,
                        selectedWorkerIds: [],
                      })
                      setActiveTab(tab.value)
                    }}
                    data-test={`assign-posts-tab-${tab.value}`}
                  >
                    {tab.label} ({workersData.counts[tab.value]})
                  </Tabs.Tab>
                ))}
              </Tabs>
              <Element textColor="grey" mt={2} textSize={7}>
                {currentTab?.explanation}
              </Element>
              {/* Liste des checkboxes avec onglets */}
              <SelectPostsList
                suggestions={{
                  workers: workersFilteredByJobTitle,
                  availablePosts: availablePostsFilteredByJobTitle,
                  jobTitles: suggestions.jobTitles,
                }}
                workersRequest={workersRequest}
                currentJobTile={values.currentJobTile}
                submitButton={{
                  label: submitButtonLabel,
                  loading: isSubmitting,
                  disabled: submitButtonDisabled,
                }}
              />

              {/* Bouton de soumission */}
              {workersFilteredByJobTitle.length > 0 && (
                <Element mt={4}>
                  <Button
                    type="submit"
                    color="primary"
                    loading={isSubmitting}
                    disabled={submitButtonDisabled}
                    fullwidth
                    data-test="auto-assign-posts-form-button"
                  >
                    {submitButtonLabel}
                  </Button>
                </Element>
              )}
              <OverlayLoader
                isLoading={isSubmitting}
                text={`Création de ${selectedPostsCount} missions en cours...`}
              />
            </Element>
          </Form>
        )
      }}
    </Formik>
  )
}

export default MultipleAssignsForm
